import type { IngestChangesetEntry } from '@axilon-apps/domains-ontology-ui-state'
import { previewDiffValue, type PropertyDiffRow } from '../../components/DiffTable'

/** Session changeset cells use ∅ for undefined (scope-start "no prior value"). */
function previewChangesetValue(v: unknown): string {
  return previewDiffValue(v, { absent: '∅' })
}

export type ChangesetDiffPresentation = {
  rows: PropertyDiffRow[]
  columns: 'before-after' | 'value'
  struck: boolean
}

/**
 * Map a folded changeset entry to PropertyDiffTable rows.
 * `created` has no per-prop payload (by design) — returns null.
 */
export function changesetEntryToDiffPresentation(
  entry: IngestChangesetEntry
): ChangesetDiffPresentation | null {
  if (entry.op === 'created') return null

  if (entry.op === 'deleted') {
    const snapshot = entry.snapshot ?? {}
    const names = Object.keys(snapshot).sort()
    if (names.length === 0) return null
    return {
      columns: 'value',
      struck: true,
      rows: names.map((name) => {
        const text = previewChangesetValue(snapshot[name])
        return { name, after: text, afterTitle: text }
      })
    }
  }

  const changes = entry.changes ?? {}
  const names = Object.keys(changes).sort()
  if (names.length === 0) return null
  return {
    columns: 'before-after',
    struck: false,
    rows: names.map((name) => {
      const { before, after } = changes[name]!
      const beforeText = previewChangesetValue(before)
      // Overwrite unsets land as `after: null` (key removed). Session cells use
      // ∅ for "no value"; keep previewDiffValue's literal `null` for other
      // consumers (gwbk diffs) by special-casing only here.
      const afterText = after === null ? '∅' : previewChangesetValue(after)
      return {
        name,
        before: beforeText,
        after: afterText,
        beforeTitle: beforeText,
        afterTitle: afterText
      }
    })
  }
}
